import { Component, OnInit, OnDestroy, Output, EventEmitter, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Observable, of } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, map, catchError } from 'rxjs/operators';
import {
    TaskFilterRequest, TaskFilterEvent, QuickFilter,
    TaskTypeResponse, TaskStatusResponse, TaskPriorityResponse, TaskCategoryResponse
} from '../../models/task.model';
import { TaskCatalogApiService } from '../../services/task-catalog-api.service';
import { EmpresaApiService } from '../../../empresas/services/empresa-api.service';
import { DealApiService } from '../../../deals/services/deal-api.service';
import { LeadApiService } from '../../../leads/services/lead-api.service';
import { UserDirectoryApiService, directoryUserLabel } from '../../../../shared/services/user-directory-api.service';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { EntityPickerComponent, PickerOption, PickerSearchPage } from '../../../../shared/components/form/entity-picker/entity-picker.component';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';

@Component({
    selector: 'app-task-filters',
    standalone: true,
    imports: [CommonModule, FormsModule, EntityPickerComponent, HasPermissionDirective],
    templateUrl: './task-filters.component.html'
})
export class TaskFiltersComponent implements OnInit, OnDestroy {
    @Output() filterChanged = new EventEmitter<TaskFilterEvent>();
    @Output() createClicked = new EventEmitter<void>();

    // Catálogos
    types: TaskTypeResponse[] = [];
    statuses: TaskStatusResponse[] = [];
    priorities: TaskPriorityResponse[] = [];
    categories: TaskCategoryResponse[] = [];

    // Estado del filtro
    showFilters = false;
    openDropdown: 'status' | 'priority' | 'type' | 'category' | 'completion' | null = null;
    searchTerm = '';
    filtroStatusId?: number;
    filtroPriorityId?: number;
    filtroTypeId?: number;
    filtroCategoryId?: number;
    filtroIsCompleted: '' | 'true' | 'false' = '';
    quickFilter: QuickFilter = 'all';

    readonly quickFilterOptions: { k: QuickFilter; l: string }[] = [
        { k: 'all', l: 'Todas' },
        { k: 'mine', l: 'Mías' },
        { k: 'urgent', l: 'Urgentes' },
        { k: 'overdue', l: 'Vencidas' },
        { k: 'today', l: 'Hoy' }
    ];

    // Filtros por asociación
    filtroCompany: PickerOption | null = null;
    filtroDeal: PickerOption | null = null;
    filtroLead: PickerOption | null = null;
    filtroOwner: PickerOption | null = null;

    private readonly destroy$ = new Subject<void>();
    private readonly searchInput$ = new Subject<string>();

    // Search functions para los pickers
    searchCompany = (term: string, page: number) => this.searchCompaniesFn(term, page);
    searchDeal = (term: string, page: number) => this.searchDealsFn(term, page);
    searchLead = (term: string, page: number) => this.searchLeadsFn(term, page);
    searchUser = (term: string, page: number) => this.searchUsersFn(term, page);

    constructor(
        private catalogApi: TaskCatalogApiService,
        private empresaApi: EmpresaApiService,
        private dealApi: DealApiService,
        private leadApi: LeadApiService,
        private userDirectory: UserDirectoryApiService,
        private authService: AuthService
    ) { }

    ngOnInit(): void {
        this.cargarCatalogos();

        this.searchInput$.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            takeUntil(this.destroy$)
        ).subscribe(() => this.emitFilter());

        // Emitir el estado inicial
        this.emitFilter();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private cargarCatalogos(): void {
        this.catalogApi.getActiveTypes().pipe(takeUntil(this.destroy$)).subscribe(d => this.types = d);
        this.catalogApi.getActiveStatuses().pipe(takeUntil(this.destroy$)).subscribe(d => this.statuses = d);
        this.catalogApi.getActivePriorities().pipe(takeUntil(this.destroy$)).subscribe(d => this.priorities = d);
        this.catalogApi.getActiveCategories().pipe(takeUntil(this.destroy$)).subscribe(d => this.categories = d);
    }

    // ===== Handlers UI =====

    toggleFilters(): void { this.showFilters = !this.showFilters; }

    toggleDropdown(name: 'status' | 'priority' | 'type' | 'category' | 'completion', ev?: Event): void {
        ev?.stopPropagation();
        this.openDropdown = this.openDropdown === name ? null : name;
    }

    @HostListener('document:click')
    onDocumentClick(): void { this.openDropdown = null; }

    buscar(): void { this.searchInput$.next(this.searchTerm); }

    buscarEnter(ev: Event): void {
        ev.preventDefault();
        this.emitFilter();
    }

    setQuickFilter(f: QuickFilter): void {
        this.quickFilter = f;
        this.emitFilter();
    }

    setStatusFilter(id: number | null): void {
        this.filtroStatusId = this.filtroStatusId === (id ?? undefined) ? undefined : (id ?? undefined);
        this.emitFilter();
    }
    setPriorityFilter(id: number | null): void {
        this.filtroPriorityId = this.filtroPriorityId === (id ?? undefined) ? undefined : (id ?? undefined);
        this.emitFilter();
    }
    setTypeFilter(id: number | null): void {
        this.filtroTypeId = this.filtroTypeId === (id ?? undefined) ? undefined : (id ?? undefined);
        this.emitFilter();
    }
    setCategoryFilter(id: number | null): void {
        this.filtroCategoryId = this.filtroCategoryId === (id ?? undefined) ? undefined : (id ?? undefined);
        this.emitFilter();
    }

    onCompanyFilterChange(opt: PickerOption | null): void { this.filtroCompany = opt; this.emitFilter(); }
    onDealFilterChange(opt: PickerOption | null): void { this.filtroDeal = opt; this.emitFilter(); }
    onLeadFilterChange(opt: PickerOption | null): void { this.filtroLead = opt; this.emitFilter(); }
    onOwnerFilterChange(opt: PickerOption | null): void { this.filtroOwner = opt; this.emitFilter(); }

    resetFilters(): void {
        this.searchTerm = '';
        this.filtroStatusId = undefined;
        this.filtroPriorityId = undefined;
        this.filtroTypeId = undefined;
        this.filtroCategoryId = undefined;
        this.filtroCompany = null;
        this.filtroDeal = null;
        this.filtroLead = null;
        this.filtroOwner = null;
        this.filtroIsCompleted = '';
        this.quickFilter = 'all';
        this.emitFilter();
    }

    get anyFilterActive(): boolean {
        return !!(
            this.searchTerm ||
            this.filtroStatusId ||
            this.filtroPriorityId ||
            this.filtroTypeId ||
            this.filtroCategoryId ||
            this.filtroCompany ||
            this.filtroDeal ||
            this.filtroLead ||
            this.filtroOwner ||
            this.filtroIsCompleted !== '' ||
            this.quickFilter !== 'all'
        );
    }

    get currentStatusLabel(): string { return this.statuses.find(s => s.id === this.filtroStatusId)?.label || 'Estado'; }
    get currentPriorityLabel(): string { return this.priorities.find(p => p.id === this.filtroPriorityId)?.label || 'Prioridad'; }
    get currentTypeLabel(): string { return this.types.find(t => t.id === this.filtroTypeId)?.label || 'Tipo'; }
    get currentCategoryLabel(): string { return this.categories.find(c => c.id === this.filtroCategoryId)?.name || 'Categoría'; }
    get currentCompletionLabel(): string {
        return this.filtroIsCompleted === 'true' ? 'Completadas' : this.filtroIsCompleted === 'false' ? 'Abiertas' : 'Completado';
    }

    emitFilter(): void {
        const user = this.authService.getCurrentUser();
        const now = new Date();
        const req: TaskFilterRequest = {
            searchTerm: this.searchTerm?.trim() || undefined,
            statusId: this.filtroStatusId,
            priorityId: this.filtroPriorityId,
            typeId: this.filtroTypeId,
            categoryId: this.filtroCategoryId,
            companyId: this.filtroCompany?.id,
            dealId: this.filtroDeal?.id,
            leadId: this.filtroLead?.id,
            ownerId: this.filtroOwner?.id,
            isCompleted: this.filtroIsCompleted === '' ? undefined : this.filtroIsCompleted === 'true'
        };

        // Lógica Quick Filter
        switch (this.quickFilter) {
            case 'mine': if (user?.id) req.ownerId = user.id; break;
            case 'overdue': req.isOverdue = true; break;
            case 'today': {
                const s = new Date(now); s.setHours(0, 0, 0, 0);
                const e = new Date(now); e.setHours(23, 59, 59, 999);
                req.dueFrom = s.toISOString(); req.dueTo = e.toISOString();
                req.isCompleted = false;
                break;
            }
            case 'thisWeek': {
                const s = new Date(now); s.setHours(0, 0, 0, 0);
                const e = new Date(now); e.setDate(e.getDate() + 7); e.setHours(23, 59, 59, 999);
                req.dueFrom = s.toISOString(); req.dueTo = e.toISOString();
                req.isCompleted = false;
                break;
            }
            case 'urgent': {
                // INC 23 — usar el NIVEL de prioridad (1=Urgente), no el priorityId del tenant.
                req.priorityLevel = 1;
                req.isCompleted = false;
                break;
            }
            case 'undated': req.isCompleted = false; break;
        }

        this.filterChanged.emit({ request: req, quickFilter: this.quickFilter });
    }

    // ===== Search Logic =====

    private searchCompaniesFn(term: string, page: number): Observable<PickerSearchPage> {
        const businessId = this.authService.getCurrentUser()?.businessId ?? 1;
        return this.empresaApi.searchCompanies({ businessId, searchTerm: term || undefined, page, size: 10 }).pipe(
            map(res => ({ items: (res.content || []).map(c => ({ id: c.id!, label: c.commercialName, sublabel: c.businessName || undefined })), totalPages: res.totalPages })),
            catchError(() => of({ items: [], totalPages: 0 }))
        );
    }
    private searchDealsFn(term: string, page: number): Observable<PickerSearchPage> {
        const businessId = this.authService.getCurrentUser()?.businessId ?? 1;
        return this.dealApi.searchDeals({ businessId, searchName: term || undefined, page, size: 10 }).pipe(
            map(res => ({ items: (res.content || []).map(d => ({ id: d.id!, label: d.name, sublabel: d.dealCycleName || d.companyName || undefined })), totalPages: res.totalPages })),
            catchError(() => of({ items: [], totalPages: 0 }))
        );
    }
    private searchLeadsFn(term: string, page: number): Observable<PickerSearchPage> {
        const businessId = this.authService.getCurrentUser()?.businessId ?? 1;
        return this.leadApi.searchLeads({ businessId, searchName: term || undefined, page, size: 10 }).pipe(
            map(res => ({ items: (res.content || []).map(l => ({ id: l.id!, label: l.name, sublabel: this.formatUserName(l.contactFullname, l.contactLastname) })), totalPages: res.totalPages })),
            catchError(() => of({ items: [], totalPages: 0 }))
        );
    }
    /** Filtro por responsable — directorio unificado con búsqueda en servidor. */
    private searchUsersFn(term: string, _page: number): Observable<PickerSearchPage> {
        return this.userDirectory.searchUsers(term || '', 20).pipe(
            map(list => ({ items: list.map(u => ({ id: u.id, label: directoryUserLabel(u), sublabel: u.mail })), totalPages: 1 })),
            catchError(() => of({ items: [], totalPages: 0 }))
        );
    }
    private formatUserName(f?: string | null, l?: string | null): string { return `${f ?? ''}${l ? ' ' + l : ''}`.trim() || '—'; }
}